import { useEffect } from 'react';
import { useDrop } from 'react-dnd';

import { FAVORITE_DRAG_TYPE, type FavoriteDragItem } from './constants';
import { isPointInRect } from './utils';

import type { RefObject } from 'react';

interface BodyDropTargetProps {
  /** 즐겨찾기 바 영역(LiWrapper) — 바 위 드롭과 진짜 바깥을 좌표로 구분하는 기준 */
  barRef: RefObject<HTMLLIElement | null>;
  /** 커서가 유효 드롭 영역(바/메뉴) 밖인지 여부를 상위로 전달 */
  onOutsideChange: (isOutside: boolean) => void;
}

/**
 * document.body를 catch-all 드롭 타깃으로 등록하는 헬퍼 컴포넌트
 * @description
 * - 더보기 버튼처럼 drop 핸들러가 없는 타깃이나 등록되지 않은 영역에 놓아도 body가 드롭을 대신 받는다.
 * - 단, 어디에 놓든 커밋하면 빈 영역에 놓아도 직전 위치로 커밋되는 문제가 있다. 따라서 body가 받은 드롭은
 *   포인터가 즐겨찾기 바 rect 안이면 커밋({dropped:true}), 진짜 바깥이면 결과 없이(undefined) 취소한다.
 *   (더보기 메뉴(DropdownMenu2)는 Popper 기반이라 backdrop이 화면을 덮지 않으며,
 *   메뉴 위 드롭은 메뉴 자신의 drop 타깃이 직접 처리하므로 여기서 다루지 않는다.)
 * - hover로는 커서가 바깥일 때를 감지해 Divider를 숨기도록 onOutsideChange를 통지한다.
 * - useDrop은 DndProvider 하위에서만 호출 가능하므로 별도 컴포넌트로 분리한다.
 */
const FavoriteBodyDropTarget = ({ barRef, onOutsideChange }: BodyDropTargetProps) => {
  const [, bodyDrop] = useDrop<FavoriteDragItem, { dropped: boolean }>({
    accept: FAVORITE_DRAG_TYPE,
    hover: (_item, monitor) => {
      // 커서가 어떤 항목/리스트 위도 아닐 때(=빈 영역)만 평가
      if (!monitor.isOver({ shallow: true })) return;
      const offset = monitor.getClientOffset();
      const rect = barRef.current?.getBoundingClientRect();
      onOutsideChange(!offset || !rect || !isPointInRect(offset.x, offset.y, rect));
    },
    drop: (_item, monitor) => {
      // 메뉴/리스트 등 더 구체적인 타깃이 이미 처리했으면 그대로 둔다
      if (monitor.didDrop()) return undefined;
      const offset = monitor.getClientOffset();
      const rect = barRef.current?.getBoundingClientRect();
      // 즐겨찾기 바 영역 위면 커밋, 진짜 바깥이면 결과 없음(undefined)으로 취소.
      // FavoriteMoreItem.end가 monitor.didDrop()을 쓰므로 취소 시 객체를 반환하면 안 된다.
      if (offset && rect && isPointInRect(offset.x, offset.y, rect)) {
        return { dropped: true };
      }
      return undefined;
    },
  });

  useEffect(() => {
    bodyDrop(document.body);
    return () => {
      bodyDrop(null);
    };
  }, [bodyDrop]);

  return null;
};

export default FavoriteBodyDropTarget;
